<script lang="ts">
  import { untrack, type Component } from 'svelte';
  import { router } from './lib/router.svelte';
  import { routes } from './routes';
  import { session } from './lib/stores/session.svelte';
  import { prefs } from './lib/prefs.svelte';
  import { account } from './lib/stores/account.svelte';
  import { data } from './lib/stores/data.svelte';
  import { timer } from './lib/stores/timer.svelte';
  import { shell } from './lib/shell/shell.svelte';
  import { t } from './lib/i18n.svelte';
  import AppShell from './lib/shell/AppShell.svelte';
  import Toaster from './lib/ui/Toaster.svelte';
  import ConfirmHost from './lib/ui/ConfirmHost.svelte';
  import Spinner from './lib/ui/Spinner.svelte';
  import Logo from './lib/shell/Logo.svelte';
  import { CookieConsent } from './lib/consent';
  import { reloadForNewVersion } from './lib/version';

  router.setRoutes(routes);

  const START_PAGES: Record<string, string> = {
    dashboard: '/app',
    timer: '/app/timer',
    entries: '/app/registraties',
    clients: '/app/klanten',
    reports: '/app/rapporten'
  };

  let page = $state<Component<any> | null>(null);
  let pageParams = $state<Record<string, string>>({});
  let layout = $state<'app' | 'public' | null>(null);
  let loadToken = 0;

  // After sign-in: preferences, account settings, data and the timer.
  session.onSignedIn(() => {
    data.hydrate();
    prefs.load();
    account.load().catch(() => {});
    data.refresh().catch(() => {});
    timer.start_sync();
  });

  function startPage(): string {
    return START_PAGES[prefs.value.startPage] || '/app';
  }

  async function resolve(path: string) {
    // Guards: app pages need a session, sign-in pages do not want one.
    if (path === '/' || path === '') {
      router.navigate(session.signedIn ? startPage() : '/login', { replace: true });
      return;
    }
    const match = router.match(path);
    if (!match) {
      router.navigate(session.signedIn ? '/app' : '/login', { replace: true });
      return;
    }
    if (match.route.layout === 'app' && !session.signedIn) {
      router.navigate(`/login?next=${encodeURIComponent(path + router.search)}`, { replace: true });
      return;
    }
    if (session.signedIn && (path === '/login' || path.startsWith('/register'))) {
      const next = router.query.get('next');
      router.navigate(next && next.startsWith('/app') ? next : startPage(), { replace: true });
      return;
    }
    const token = ++loadToken;
    let mod;
    try {
      mod = await match.route.load();
    } catch {
      // The page's script is gone (new version deployed): load the new one.
      reloadForNewVersion();
      return;
    }
    if (token !== loadToken) return;
    if (match.route.title) shell.title = t(match.route.title);
    pageParams = match.params;
    layout = match.route.layout;
    page = mod.default;
  }

  let ready = $state(false);
  $effect(() => {
    // Initial session check, then follow the URL.
    session.check().finally(() => (ready = true));
  });
  $effect(() => {
    if (!ready) return;
    const path = router.path;
    void session.signedIn;
    untrack(() => resolve(path));
  });

  router.start();
</script>

{#if !ready || !page}
  <div class="boot">
    <Logo size={40} wordmark={false} />
    <Spinner size={20} />
  </div>
{:else if layout === 'app'}
  {@const Page = page}
  <AppShell>
    {#key router.path}
      <Page params={pageParams} />
    {/key}
  </AppShell>
{:else}
  {@const Page = page}
  <Page params={pageParams} />
{/if}

<Toaster />
<ConfirmHost />
<CookieConsent />

<style>
  .boot { min-height: 100vh; min-height: 100dvh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-5); color: var(--text-3); }
</style>
